import React, { useCallback, useState, useEffect } from 'react';
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { connect } from 'react-redux';
import { State } from 'country-state-city';

import ModernHeader from '../../../../components/layout/Headers/ModernHeader';
import { ROUTES } from '../../../../shared/constants/routes';
import {
  DEFAULT_FORM_VALUES,
  FEDERAL_TAX_CLASSIFICATIONS,
  TIN_TYPES,
  buildW9FormValuesFromApi,
  getTinPlaceholder,
  formatTinNumber,
  shouldSuppressLoadError,
} from '../../../../shared/constants/EarningsPayout';
import { createW9Form, getW9Form } from '../../../../shared/actions/transactionAction';
import { showSuccess, showError } from '../../../../shared/utils/swalHandler';
import { useNavigation } from '../../../../shared/utils/useNavigation';
import { encryptValue } from '../../../../shared/utils/config/encryption';
import { getPendingSigningUrl } from '../../../../shared/utils/function';
import {
  Form,
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormControlWithFocus,
  FormMessage,
  useFormValidation,
} from "../../../../shared/components/form";
import { taxInfoSchema, formatErrorResponse } from '../../../../shared/validations/taxInfo';
import TaxImportantNoticeCard from './Card/TaxImportantNoticeCard';
import TaxBanditsSignatureRequiredCard from './Card/TaxBanditsSignatureRequiredCard';
import CompleteFormModal from '../../../Modals/EarningsPayout/CompleteFormModal';
import TaxSecureSubmissionCard from './Card/TaxSecureSubmissionCard';

const TaxInformation = ({
  user
}) => {
  const navigate = useNavigation();
  const [isLoading, setIsLoading] = useState(true);
  const [stateOptions, setStateOptions] = useState([]);
  const [alreadyFilled, setAlreadyFilled] = useState(false);
  const [pendingSigningUrl, setPendingSigningUrl] = useState(null);
  const [showCompleteFormModal, setShowCompleteFormModal] = useState(false);
  const isUsUser = true;

  const form = useForm({
    resolver: zodResolver(taxInfoSchema),
    defaultValues: DEFAULT_FORM_VALUES,
    mode: "onChange",
  });

  // Load state/province options based on user's country using external package
  useEffect(() => {
    const rawCountry = (user?.country || '').toUpperCase();
    if (!rawCountry) {
      setStateOptions([]);
      return;
    }

    // Normalize and restrict to supported countries
    const normalizedCountry =
      rawCountry === 'USA' ? 'US' : rawCountry;

    const states = State.getStatesOfCountry(normalizedCountry) || [];
    const options = states.map((state) => ({
      code: state.isoCode,
      name: state.name,
    }));

    setStateOptions(options);
  }, [user?.country]);

  // Fetch W-9 form data on component mount
  useEffect(() => {
    let isActive = true;

    const fetchW9FormData = async () => {
      setIsLoading(true);
      try {
        const response = await getW9Form();
        if (!isActive) return;

        if (!response?.success) {
          setAlreadyFilled(false);
          setPendingSigningUrl(null);
          setShowCompleteFormModal(false);
          form.reset(DEFAULT_FORM_VALUES);
          if (response?.error) {
            // Non-blocking: user can still fill the form.
            if (!shouldSuppressLoadError(response.error)) {
              showError(
                'Unable to load saved W‑9',
                `${response.error} You can still fill and submit the form below.`
              );
            }
          }
          return;
        }

        // API returns: response.data = { status, data, ... }
        const formData = response?.data?.data;
        const hasFormData =
          formData && typeof formData === 'object' && Object.keys(formData).length > 0;

        setAlreadyFilled(Boolean(hasFormData));
        setPendingSigningUrl(hasFormData ? getPendingSigningUrl(formData?.taxBanditsResponse) : null);
        setShowCompleteFormModal(false);
        form.reset(hasFormData ? buildW9FormValuesFromApi(formData) : DEFAULT_FORM_VALUES);
      } catch (error) {
        if (!isActive) return;
        setAlreadyFilled(false);
        setPendingSigningUrl(null);
        setShowCompleteFormModal(false);
        form.reset(DEFAULT_FORM_VALUES);
        showError('Error', error?.message || 'Failed to load W-9 form. Please try again.');
      } finally {
        if (isActive) setIsLoading(false);
      }
    };

    fetchW9FormData();
    return () => {
      isActive = false;
    };
  }, [form]);

  const { validateAndFocus, shouldShowError } = useFormValidation(form, {
    showErrorsOnSubmit: true,
    focusOnFirstError: true,
  });

  const tinType = form.watch('tinType');
  const { isSubmitting, isValid } = form.formState;
  const isSubmitDisabled = isSubmitting || !isValid;

  const onSubmit = useCallback(async (data) => {
    try {
      // remove certification field from data
      const { certification, ...rest } = data;

      // Encrypt tinNumber before sending to backend
      if (rest.tinNumber) {
        rest.tinNumber = await encryptValue(rest.tinNumber);
      }

      const response = await createW9Form(rest);
      if (response?.success) {
        const taxBanditsResponse = response?.data?.taxBanditsResponse;
        const signingUrl = getPendingSigningUrl(taxBanditsResponse);

        if (signingUrl) {
          setPendingSigningUrl(signingUrl);
          setShowCompleteFormModal(true);
        } else {
          const successMessage = response?.message || 'W-9 form saved successfully.';
          await showSuccess('Success', successMessage);
          navigate.toEarningsPayout();
        }
      } else {
        // Format error response with detailed validation errors if available
        const formattedErrorMessage = formatErrorResponse(response);
        await showError('Error', formattedErrorMessage);
      }
    } catch (error) {
      // Handle unexpected errors during form submission
      const errorMessage = error?.message ||
        error?.response?.data?.message ||
        'An unexpected error occurred. Please try again.';
      await showError('Error', errorMessage);
    }
  }, [navigate]);

  const handleFormSubmit = useCallback(
    async (data) => {
      const isValid = await validateAndFocus();
      if (!isValid) return;
      await onSubmit(data);
    },
    [onSubmit, validateAndFocus]
  );

  // const handleDownload = () => {
  //   if (!pdfUrl) return;
  //   downloadFileFromUrl(pdfUrl, 'W-9.pdf');
  // };

  return (
    <div className="modern-earnings-payout-page">
      <ModernHeader
        customTitle="W-9 Tax Form"
        showButtonBorder={false}
        hideSearchIcon
        showAvatar={false}
        showBackButton
        backButtonUrl={ROUTES.EARNINGS_PAYOUT}
      // {...pdfUrl && {
      //   showIconInRight: 'fa-solid fa-download',
      //   onShowIconInRightClick: handleDownload
      // }}
      />

      {/* Important Notice Card */}
      <TaxImportantNoticeCard
        isUsUser={isUsUser}
      />

      {/* Signature Required Card */}
      <TaxBanditsSignatureRequiredCard
        pendingSigningUrl={pendingSigningUrl}
        isUsUser={isUsUser}
      />

      <CompleteFormModal
        isOpen={showCompleteFormModal}
        signingUrl={pendingSigningUrl}
        isUsUser={isUsUser}
      />

      {isLoading ? (
        <div className="bank-account-loading-container">
          <i className="fas fa-spinner fa-spin bank-account-loading-spinner" />
          <p className="bank-account-loading-text">
            Loading information...
          </p>
        </div>
      ) : (
        <Form {...form}>
          <form
            className="cm_dark_form"
            onSubmit={form.handleSubmit(handleFormSubmit)}
          >
            {/* Personal Information Section */}
            <section>
              <h2 className="mb-0">Personal Information</h2>
              <p className="f_12_gray mb-3">
                Enter your personal details as they appear on your tax documents
              </p>

              <div className="form-group">
                <FormLabel>Name (as shown on your income tax return) *</FormLabel>
                <div className="d-flex gap-2">
                  <FormField
                    control={form.control}
                    name="firstName"
                    render={({ field }) => (
                      <FormItem className="w-full">
                        <FormControlWithFocus fieldName="firstName">
                          <input
                            type="text"
                            className={`form-control cm_form_control ${shouldShowError('firstName') ? 'is-invalid' : ''}`}
                            placeholder="First Name"
                            disabled={alreadyFilled}
                            {...field}
                          />
                        </FormControlWithFocus>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="lastName"
                    render={({ field }) => (
                      <FormItem className="w-full">
                        <FormControlWithFocus fieldName="lastName">
                          <input
                            type="text"
                            className={`form-control cm_form_control ${shouldShowError('lastName') ? 'is-invalid' : ''}`}
                            placeholder="Last Name"
                            disabled={alreadyFilled}
                            {...field}
                          />
                        </FormControlWithFocus>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
              </div>

              <FormField
                control={form.control}
                name="businessName"
                render={({ field }) => (
                  <FormItem className="form-group">
                    <FormLabel>Business name/disregarded entity name (if different from above)</FormLabel>
                    <FormControl>
                      <input
                        type="text"
                        className={`form-control cm_form_control ${shouldShowError('businessName') ? 'is-invalid' : ''}`}
                        placeholder="Business or Entity Name"
                        disabled={alreadyFilled}
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="federalTaxClassification"
                render={({ field }) => (
                  <FormItem className="form-group">
                    <FormLabel>Federal tax classification *</FormLabel>
                    <FormControl>
                      <div className='pl-4'>
                        {FEDERAL_TAX_CLASSIFICATIONS.map((item, index) => (
                          <p key={index} className="cm_span_label d-flex align-items-center gap-3">
                            <input
                              type="radio"
                              id={`tax-class-${index}`}
                              name="federalTaxClassification"
                              value={item.value}
                              checked={field.value === item.value}
                              onChange={(e) => {
                                field.onChange(e.target.value);
                                if (e.target.value !== 'Other') {
                                  form.setValue('otherTaxClassification', '', {
                                    shouldDirty: true,
                                    shouldValidate: true,
                                  });
                                }
                              }}
                              disabled={alreadyFilled}
                              className={`form-check-input ${alreadyFilled && field.value === item.value ? 'taxinfo-radio-disabled-checked' : ''}`}
                            />
                            <label htmlFor={`tax-class-${index}`} style={{ margin: 0, cursor: 'pointer' }}>
                              {item.label}
                            </label>
                            {item.value === 'Other' && field.value === 'Other' && (
                              <FormField
                                control={form.control}
                                name="otherTaxClassification"
                                render={({ field: otherField }) => (
                                  <FormItem>
                                    <FormControl>
                                      <input
                                        type="text"
                                        className={`form-control cm_form_control form-control-sm ${shouldShowError('otherTaxClassification') ? 'is-invalid' : ''}`}
                                        placeholder="Specify tax classification"
                                        disabled={alreadyFilled}
                                        {...otherField}
                                      />
                                    </FormControl>
                                    <FormMessage />
                                  </FormItem>
                                )}
                              />
                            )}
                          </p>
                        ))}
                      </div>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </section>

            <div className="cm_hr_full"></div>

            {/* Address Information Section */}
            <section>
              <h2 className="mb-0">Address Information</h2>
              <p className="f_12_gray mb-3">Provide your current mailing address</p>

              <FormField
                control={form.control}
                name="address"
                render={({ field }) => (
                  <FormItem className="form-group">
                    <FormLabel>Address (number, street, and apt. or suite no.) *</FormLabel>
                    <FormControlWithFocus fieldName="address">
                      <input
                        type="text"
                        className={`form-control cm_form_control ${shouldShowError('address') ? 'is-invalid' : ''}`}
                        placeholder="Street Address"
                        disabled={alreadyFilled}
                        {...field}
                      />
                    </FormControlWithFocus>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="city"
                render={({ field }) => (
                  <FormItem className="form-group">
                    <FormLabel>City *</FormLabel>
                    <FormControlWithFocus fieldName="city">
                      <input
                        type="text"
                        className={`form-control cm_form_control ${shouldShowError('city') ? 'is-invalid' : ''}`}
                        placeholder="City"
                        disabled={alreadyFilled}
                        {...field}
                      />
                    </FormControlWithFocus>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <div className="form-group">
                <FormLabel>State &amp; ZIP Code *</FormLabel>
                <div className="d-flex gap-2">
                  <FormField
                    control={form.control}
                    name="state"
                    render={({ field }) => (
                      <FormItem className="w-full">
                        <FormControlWithFocus fieldName="state">
                          <select
                            className={`form-control cm_form_control ${shouldShowError('state') ? 'is-invalid' : ''}`}
                            value={field.value}
                            onChange={(e) => field.onChange(e.target.value)}
                            disabled={alreadyFilled}
                          >
                            <option value="">Select State</option>
                            {stateOptions.map((state) => (
                              <option key={state.code} value={state.code}>
                                {state.name}
                              </option>
                            ))}
                          </select>
                        </FormControlWithFocus>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="zipCode"
                    render={({ field }) => (
                      <FormItem className="w-full">
                        <FormControlWithFocus fieldName="zipCode">
                          <input
                            type="text"
                            className={`form-control cm_form_control ${shouldShowError('zipCode') ? 'is-invalid' : ''}`}
                            placeholder="ZIP/Postal Code"
                            maxLength={16}
                            disabled={alreadyFilled}
                            {...field}
                          />
                        </FormControlWithFocus>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
              </div>
            </section>

            <div className="cm_hr_full"></div>

            {/* Taxpayer Identification Number Section */}
            <section>
              <h2 className="mb-0">Taxpayer Identification Number</h2>
              <p className="f_12_gray mb-3">
                Enter your Social Security Number or Individual Taxpayer Identification Number
              </p>

              <FormField
                control={form.control}
                name="tinType"
                render={({ field }) => (
                  <FormItem className="form-group">
                    <FormLabel>Select your TIN type *</FormLabel>
                    <FormControl>
                      <div className='pl-4'>
                        {TIN_TYPES.map((item, index) => (
                          <p key={index} className="cm_span_label">
                            <input
                              type="radio"
                              id={`tin-type-${index}`}
                              name="tinType"
                              value={item.value}
                              checked={field.value === item.value}
                              onChange={(e) => {
                                field.onChange(e.target.value);
                                form.setValue('tinNumber', '', {
                                  shouldDirty: true,
                                  shouldValidate: true,
                                });
                              }}
                              disabled={alreadyFilled}
                              className={`form-check-input ${alreadyFilled && field.value === item.value ? 'taxinfo-radio-disabled-checked' : ''}`}
                            />
                            <label htmlFor={`tin-type-${index}`} style={{ margin: 0, cursor: 'pointer' }}>
                              {item.label}
                            </label>
                          </p>
                        ))}
                      </div>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="tinNumber"
                render={({ field }) => (
                  <FormItem className="form-group">
                    <FormLabel>Taxpayer Identification Number *</FormLabel>
                    <FormControlWithFocus fieldName="tinNumber">
                      <input
                        type="text"
                        className={`form-control cm_form_control ${shouldShowError('tinNumber') ? 'is-invalid' : ''}`}
                        placeholder={getTinPlaceholder(tinType)}
                        disabled={alreadyFilled || !tinType}
                        value={field.value}
                        onChange={(e) => {
                          const formatted = formatTinNumber(e.target.value, tinType);
                          field.onChange(formatted);
                        }}
                        onBlur={field.onBlur}
                        ref={field.ref}
                      />
                    </FormControlWithFocus>
                    <FormMessage />
                    <small className="cm_sm_text">Select a TIN type to see format</small>
                  </FormItem>
                )}
              />
            </section>


            {/* Certification Section */}
            {!alreadyFilled && (
              <>
                <div className="cm_hr_full"></div>
                <section>
                  <h2 className="mb-0">Certification</h2>
                  <p className="f_12_gray mb-3">Confirm the information you provided is accurate</p>
                  <FormField
                    control={form.control}
                    name="certification"
                    render={({ field }) => (
                      <FormItem className="cm_term_check_wrap">
                        <FormControl>
                          <label className="flex align-items-baseline gap-3" style={{ cursor: 'pointer' }}>
                            <input
                              type="checkbox"
                              checked={field.value}
                              onChange={(e) => field.onChange(e.target.checked)}
                              onBlur={field.onBlur}
                              ref={field.ref}
                              disabled={alreadyFilled}
                            />
                            <span>
                              I certify that all information provided is true, correct, and complete
                              under penalties of perjury
                            </span>
                          </label>
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </section>
                <div className="cm_hr_full"></div>
              </>
            )}

            {/* Secure Submission Notice */}
            <TaxSecureSubmissionCard />

            {!alreadyFilled && (
              <button
                type="submit"
                className="btn btn_primary rounded_8 w-full"
                disabled={isSubmitDisabled}
                aria-disabled={isSubmitDisabled}
              >
                {isSubmitting ? 'Submitting...' : 'Submit W-9 Form'}
              </button>
            )}
          </form>
        </Form>
      )}
    </div>
  );
};

const mapStateToProps = (state) => ({
  user: state.user?.user
});

export default connect(mapStateToProps)(TaxInformation);